import React, { useState } from 'react';
import { Spinner, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { useQuery } from 'react-query';
import { Get } from '@app/utils/httpProvider';
import {
    DANHSACH_DUYET_NGOAINGU_API, ACCOUNT_API
} from '@app/utils/apiUrl';

const accountInfoCache = {};

const fetchAllItems = async (pageIndex, pageSize) => {
    const res = await Get(`${DANHSACH_DUYET_NGOAINGU_API.GET_THONGKE_NGOAINGU}?pageIndex=1&pageSize=1000000`);
    const data = res?.data || res;
    if (!data || !data.donViStats) return data;

    const rawStats = data.donViStats.$values || data.donViStats || [];
    const uniqueAccIds = [...new Set(rawStats.map(x => x.tenDonVi || x.TenDonVi))];
    const accDonViMap = {};
    
    const idsToFetch = uniqueAccIds.filter(id => !accountInfoCache[id]);
    
    if (idsToFetch.length > 0) {
        try {
            const payload = { PageIndex: 1, PageSize: 1000000 };
            const allRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, payload);
            const allList = allRes?.data?.$values || allRes?.data?.data || allRes?.data || allRes || [];
            
            if (Array.isArray(allList)) {
                allList.forEach(info => {
                    const accId = info?.accountId || info?.AccountId;
                    if (!accId) return;
                    const donVis = info?.donVis || info?.DonVis?.$values || info?.DonVis || [];
                    const tenDonVi = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : 'Chưa có đơn vị';
                    accountInfoCache[accId] = tenDonVi;
                });
            }
            
            idsToFetch.forEach(id => {
                if (!accountInfoCache[id]) {
                    accountInfoCache[id] = 'Chưa có đơn vị';
                }
            });
        } catch (e) {
            console.error('Lỗi lấy AccountInfo hàng loạt:', e);
            idsToFetch.forEach(id => {
                accountInfoCache[id] = 'Chưa có đơn vị';
            });
        }
    }

    uniqueAccIds.forEach((accId) => {
        accDonViMap[accId] = accountInfoCache[accId] || 'Chưa có đơn vị';
    });
    
    const groupedMap = {};
    for (let stat of rawStats) {
        const accId = stat.tenDonVi || stat.TenDonVi;
        const realDonVi = accDonViMap[accId] || 'Chưa có đơn vị';
        
        if (!groupedMap[realDonVi]) {
            groupedMap[realDonVi] = {
                tenDonVi: realDonVi,
                soNguoi: 0,
                soPhuHop: 0,
                soKhongPhuHop: 0,
                soChuaDuyet: 0
            };
        }
        
        groupedMap[realDonVi].soNguoi += (stat.soNguoi || stat.SoNguoi || 0);
        groupedMap[realDonVi].soPhuHop += (stat.soPhuHop || stat.SoPhuHop || 0);
        groupedMap[realDonVi].soKhongPhuHop += (stat.soKhongPhuHop || stat.SoKhongPhuHop || 0);
        groupedMap[realDonVi].soChuaDuyet += (stat.soChuaDuyet || stat.SoChuaDuyet || 0);
    }
    
    let groupedList = Object.values(groupedMap).sort((a, b) => b.soNguoi - a.soNguoi);
    
    data.totalDonVi = groupedList.length;
    data.donViStats = groupedList.slice((pageIndex - 1) * pageSize, pageIndex * pageSize);
    
    return data;
};

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, subtitle, bg }) => (
    <div
        style={{
            background: bg || 'linear-gradient(135deg,#667eea,#764ba2)',
            borderRadius: 18,
            padding: '22px 26px',
            color: '#fff',
            boxShadow: '0 6px 24px rgba(0,0,0,0.13)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            height: '100%',
            minHeight: 130,
            position: 'relative',
            overflow: 'hidden',
        }}
    >
        {/* Icon nền mờ góc phải */}
        <div style={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 56,
            opacity: 0.18,
            lineHeight: 1,
            pointerEvents: 'none',
        }}>
            {icon}
        </div>

        {/* Icon nhỏ hiển thị */}
        <div style={{ fontSize: 26, opacity: 0.9, lineHeight: 1 }}>{icon}</div>
        <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.88, letterSpacing: 0.3, marginTop: 2 }}>
            {label}
        </div>
        <div style={{ fontSize: 46, fontWeight: 800, lineHeight: 1.05, letterSpacing: -1 }}>
            {value}
        </div>
        {subtitle && (
            <div style={{ fontSize: 11.5, opacity: 0.78, marginTop: 2 }}>{subtitle}</div>
        )}
    </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const ThongKe = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const { data, isLoading, isError } = useQuery(
        ['thongKeNgoaiNgu', currentPage],
        () => fetchAllItems(currentPage, itemsPerPage),
        { cacheTime: 0, staleTime: 0, keepPreviousData: true }
    );

    const {
        soHoSo = 0,
        totalVBCC = 0,
        hoSoPhuHop = 0,
        hoSoKhongPhuHop = 0,
        hoSoChuaDuyet = 0,
        totalDonVi = 0,
        donViStats = []
    } = data || {};

    const totalPages = Math.ceil(totalDonVi / itemsPerPage);

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted fw-semibold">Đang tải dữ liệu thống kê...</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5 text-danger">
                <i className="fas fa-exclamation-triangle fa-2x mb-2" />
                <p>Không thể tải dữ liệu thống kê. Vui lòng thử lại.</p>
            </div>
        );
    }

    return (
        <div className="py-3">
            {/* ── KPI Cards ── */}
            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="👤"
                        label="Tổng số hồ sơ đã cập nhật"
                        value={soHoSo}
                        subtitle={`${totalVBCC} văn bằng / chứng chỉ`}
                        bg="linear-gradient(135deg,#667eea,#764ba2)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="✅"
                        label="Tổng số hồ sơ Phù hợp"
                        value={hoSoPhuHop}
                        subtitle="Có ít nhất 1 văn bằng/chứng chỉ Đạt yêu cầu"
                        bg="linear-gradient(135deg,#11998e,#38ef7d)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="❌"
                        label="Tổng số hồ sơ Không phù hợp"
                        value={hoSoKhongPhuHop}
                        subtitle="Tất cả văn bằng & chứng chỉ đều Không đạt"
                        bg="linear-gradient(135deg,#e53935,#e35d5b)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="⏳"
                        label="Tổng số hồ sơ Chưa duyệt"
                        value={hoSoChuaDuyet}
                        subtitle="Có ít nhất 1 văn bằng/chứng chỉ Chưa được duyệt"
                        bg="linear-gradient(135deg,#ff9800,#e65c00)"
                    />
                </Col>
            </Row>


            {/* ── Bảng theo đơn vị ── */}
            <Card className="shadow-sm border-0" style={{ borderRadius: 14 }}>
                <Card.Body>
                    <h6 className="fw-bold mb-3 text-dark">
                        <i className="fas fa-building me-2 text-primary" />
                        Số lượng người nộp hồ sơ theo đơn vị
                    </h6>
                    {donViStats.length === 0 ? (
                        <div className="text-center text-muted py-4">Không có dữ liệu</div>
                    ) : <div className="table-responsive">
                        <Table bordered hover className="align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                            <thead className="table-light">
                                <tr className="text-nowrap fw-bold">
                                    <th className="text-center" style={{ width: 50, fontWeight: 700 }}>STT</th>
                                    <th className="text-start" style={{ fontWeight: 700 }}>Đơn vị</th>
                                    <th className="text-center" style={{ minWidth: 120, fontWeight: 700 }}>Số người nộp</th>
                                    <th className="text-center" style={{ minWidth: 140, fontWeight: 700 }}>Số người phù hợp</th>
                                    <th className="text-center" style={{ minWidth: 160, fontWeight: 700 }}>Số người Không phù hợp</th>
                                    <th className="text-center" style={{ minWidth: 160, fontWeight: 700 }}>Số người chưa duyệt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {donViStats.map((row, idx) => {
                                    const stt = (currentPage - 1) * itemsPerPage + idx + 1;
                                    return (
                                        <tr key={row.tenDonVi} style={{ color: '#000' }}>
                                            <td className="text-center" style={{ color: '#000' }}>{stt}</td>
                                            <td style={{ color: '#000' }}>
                                                <span className="fw-semibold">{row.tenDonVi}</span>
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#667eea', borderRadius: 50 }}>
                                                    {row.soNguoi}
                                                </Badge>
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soPhuHop > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#11998e', borderRadius: 50 }}>
                                                        {row.soPhuHop}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soKhongPhuHop > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#e53935', borderRadius: 50 }}>
                                                        {row.soKhongPhuHop}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soChuaDuyet > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#ff9800', borderRadius: 50 }}>
                                                        {row.soChuaDuyet}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>

                        {/* Phân trang đã được cập nhật */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-end mt-3">
                                <div
                                    className="dataTables_paginate paging_simple_numbers"
                                    id="example2_paginate"
                                >
                                    <Pagination
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        innerClass="pagination pagination-sm no-margin pull-right"
                                        activePage={currentPage}
                                        itemsCountPerPage={Number(
                                            itemsPerPage
                                        )}
                                        totalItemsCount={totalDonVi}
                                        pageRangeDisplayed={5}
                                        onChange={(number) =>
                                            setCurrentPage(number)
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>}
                </Card.Body>
            </Card>

            <style>{`
                .thongke-section-title {
                    font-size: 0.8rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: #6c757d;
                    margin-bottom: 12px;
                }

                /* Pagination styling */
                .pagination .page-link {
                    color: #0d6efd;
                    border-radius: 6px;
                    margin: 0 2px;
                    border: 1px solid #dee2e6;
                    padding: 5px 11px;
                    font-weight: 500;
                    transition: all 0.18s ease;
                }
                .pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                    color: #fff;
                }
                .pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    background-color: #fff;
                    border-color: #dee2e6;
                }
                .pagination .page-link:hover:not(.disabled) {
                    background-color: #e9ecef;
                    border-color: #dee2e6;
                }
            `}</style>
        </div>
    );
};

export default ThongKe;